/**
 * usePublicSessionLink.js : chargement de la page du lien de séance (D247).
 *
 * Séance, toutes les pages de mesures et, en FR, les modèles du résumé, avant
 * tout affichage (D235). Une seule analyse (adaptateur + buildPeriodAnalysis)
 * nourrit la page et son PDF. Toute erreur d'une route publique remplace la
 * page par un message, sans aucune donnée.
 */
import { useEffect, useMemo, useState } from 'react';
import { adaptLinkedSession, buildLinkedAnalysis } from './publicLinkAdapter';
import { fetchLinkedReadings, fetchLinkedSession, fetchLinkedTemplates } from './publicSessionLinkService';

export function usePublicSessionLink(token) {
  const [state, setState] = useState({ status: 'loading', errorCode: null, payload: null, readings: [], templates: null });

  useEffect(() => {
    if (!token) {
      setState({ status: 'error', errorCode: 'LINK_NOT_FOUND', payload: null, readings: [], templates: null });
      return undefined;
    }
    const controller = new AbortController();
    const { signal } = controller;
    setState((prev) => ({ ...prev, status: 'loading', errorCode: null }));
    (async () => {
      try {
        const payload = await fetchLinkedSession(token, signal);
        const [readingsResult, templates] = await Promise.all([
          fetchLinkedReadings(token, signal),
          fetchLinkedTemplates(token, signal),
        ]);
        if (signal.aborted) return;
        setState({ status: 'ready', errorCode: null, payload, readings: readingsResult.readings, templates });
      } catch (err) {
        if (signal.aborted || err?.name === 'AbortError') return;
        setState({ status: 'error', errorCode: err?.code || 'SERVER_ERROR', payload: null, readings: [], templates: null });
      }
    })();
    return () => controller.abort();
  }, [token]);

  // `session` : séance adaptée (en-tête, informations du PDF) ; `analysis` : page et PDF.
  const { session, analysis } = useMemo(() => {
    if (!state.payload) return { session: null, analysis: null };
    return {
      session: adaptLinkedSession(state.payload, state.readings),
      analysis: buildLinkedAnalysis(state.payload, state.readings),
    };
  }, [state.payload, state.readings]);

  return { ...state, session, analysis };
}
